import { StateKeys } from "@intelligems/ig-plugin/core/constants";
import mixpanel from "mixpanel-browser";
import {
  PluginStateController,
  PluginStateEventMap,
  PluginStateURLOptions,
} from "@src/components/ReverseProxy/PluginStateController";

export interface ExperienceBuilderURLOptions extends PluginStateURLOptions {
  cacheKey?: string;
  /**
   * Demo-mode bundle URL for the reverse proxy's `ig-proxy-bundle` param:
   * the plugin bundle to inject into stores that don't embed it. Empty string
   * when not in demo mode — the param is still sent (empty) so the proxy clears
   * any stale bundle cookie.
   */
  bundleUrl?: string;
}

export class ExperienceBuilderController extends PluginStateController<
  PluginStateEventMap,
  ExperienceBuilderURLOptions
> {
  override resolveURL(options: ExperienceBuilderURLOptions) {
    const url = super.resolveURL(options);
    if (!url) {
      return undefined;
    }

    url.searchParams.set(StateKeys.EMBEDDED_PREVIEW_QUERY_PARAM, "");
    if (options.cacheKey) {
      url.searchParams.set(StateKeys.EMBEDDED_PREVIEW_CACHE, options.cacheKey);
    }

    // Always sent: a value in demo mode tells the proxy which bundle to inject;
    // empty otherwise so the proxy clears any stale bundle cookie.
    url.searchParams.set("ig-proxy-bundle", options.bundleUrl ?? "");

    // Use the exact token the parent instance initialized with so the iframe
    // shares the same Mixpanel project — the cross-origin handshake requires
    // parent and child tokens to match.
    const mixPanelToken = mixpanel.get_config("token");

    url.searchParams.set("ig-mp-token", mixPanelToken);
    url.searchParams.set("ig-mp-user", mixpanel.get_distinct_id());
    url.searchParams.set("ig-mp-parent-origin", window.location.origin);

    // Cross-origin iframe recording (Mixpanel >= 2.77): the iframe is served from
    // a per-customer isolation subdomain (`<store>.rproxyserv.com`), so its exact
    // origin is only known here, when the iframe URL is built. Mixpanel's parent
    // handshake handler validates the iframe's origin against this allowlist with
    // an exact match (no wildcards) and is wired up once, the first time recording
    // starts with a non-empty allowlist — so we must NOT seed the allowlist at
    // init (it would lock onto the wrong, bare proxy origin). Instead set the exact
    // origin now and (re)start recording so the handshake listener binds to it
    // before the iframe's handshake retries elapse.
    this.allowIframeOriginForRecording(url.origin);

    return url;
  }

  private allowedIframeOrigins = new Set<string>();

  private allowIframeOriginForRecording(origin: string) {
    if (this.allowedIframeOrigins.has(origin)) {
      return;
    }
    this.allowedIframeOrigins.add(origin);
    mixpanel.set_config({
      record_allowed_iframe_origins: [...this.allowedIframeOrigins],
    });
    // The rrweb recorder bakes `recordCrossOriginIframes` (derived from a
    // non-empty allowlist) at recording START and does not re-read it on a plain
    // resume. The parent's recording began at init with an empty allowlist, so a
    // plain start_session_recording() leaves the recorder in non-cross-origin
    // mode and the iframe never stitches into the replay. Fully restart it now —
    // once the iframe's exact origin is known — so the recorder is recreated with
    // cross-origin iframe stitching enabled.
    const stopped = mixpanel.stop_session_recording() as unknown as
      | Promise<unknown>
      | undefined;
    void Promise.resolve(stopped).then(() =>
      mixpanel.start_session_recording()
    );
  }
}
